import React, { useState, useEffect, useCallback } from "react";
import "./ReminderPopup.css";
import axiosInstance from "../axiosinstance";

const TodaysReminderPopup = ({
  isOpen,
  onClose,
  invoice_id,
  family_id_for_count,
  use_family_count_api = false,
}) => {
  const [clientReply, setClientReply] = useState("");
  const [nextReminderDate, setNextReminderDate] = useState("");
  const [reminderNumber, setReminderNumber] = useState(null); // State to store reminder number
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = useCallback(() => {
    setClientReply("");
    setNextReminderDate("");
    setReminderNumber(null);
    setErrors({
      nextReminderDate: "",
      clientReply: "",
      pastDate: "",
      apiError: "",
    });
  }, []);

  const [errors, setErrors] = useState({
    nextReminderDate: "",
    clientReply: "",
    pastDate: "",
    apiError: "",
  });

  useEffect(() => {
    if (isOpen) {
      // Fresh form every time it opens
      resetForm();
      fetchReminderNumber();
    } else {
      // Also clear when closing to avoid stale data
      resetForm();
    }
    // Also re-run when selected ids change
  }, [isOpen, resetForm, invoice_id, family_id_for_count, use_family_count_api]);

  const fetchReminderNumber = async () => {
    try {
      const endpoint =
        use_family_count_api && family_id_for_count
          ? `/api/get_family_reminder_number/${family_id_for_count}`
          : `/api/get_reminder_number/${invoice_id}`;

      const response = await axiosInstance.get(endpoint);
      const payload = response?.data;

      if (Array.isArray(payload)) {
        setReminderNumber(payload[0]?.reminder_number || 0);
      } else {
        setReminderNumber(payload?.reminder_number || 0);
      }
    } catch (error) {
      console.error("Error fetching reminder number:", error);
      setReminderNumber(0);
    }
  };

  const handleSave = async () => {
    // Reset errors
    setErrors({
      nextReminderDate: "",
      clientReply: "",
      pastDate: "",
      apiError: "",
    });

    let hasErrors = false;
    const newErrors = {
      nextReminderDate: "",
      clientReply: "",
      pastDate: "",
      apiError: "",
    };

    // Validate client reply
    if (!clientReply.trim()) {
      newErrors.clientReply = "Please enter a client reply";
      hasErrors = true;
    }

    // Validate date format and past date (only if date is entered)
    let formattedDate = null;

    if (nextReminderDate.trim()) {
      // Check format DD-MM-YYYY
      const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$/;
      const match = nextReminderDate.match(dateRegex);

      if (!match) {
        newErrors.pastDate = "Please enter date in DD-MM-YYYY format";
        hasErrors = true;
      } else {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);

        // Basic validation for valid date
        if (month < 1 || month > 12 || day < 1 || day > 31) {
          newErrors.pastDate = "Please enter a valid date";
          hasErrors = true;
        } else {
          // Create date object (month is 0-indexed)
          const enteredDate = new Date(year, month - 1, day);
          enteredDate.setHours(0, 0, 0, 0);

          // Check if date is valid (handles invalid dates like 31-02-2025)
          if (
            enteredDate.getDate() !== day ||
            enteredDate.getMonth() !== month - 1 ||
            enteredDate.getFullYear() !== year
          ) {
            newErrors.pastDate = "Please enter a valid date";
            hasErrors = true;
          } else {
            // Check for past date
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (enteredDate < today) {
              newErrors.pastDate = "Reminder date cannot be in the past";
              hasErrors = true;
            } else {
              // Convert to YYYY-MM-DD for backend
              formattedDate = `${year}-${String(month).padStart(
                2,
                "0"
              )}-${String(day).padStart(2, "0")}`;
            }
          }
        }
      }
    }

    // Stop here if any validation error
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true); // Start loader

    try {
      const isFamilyFlow = use_family_count_api && Boolean(family_id_for_count);
      const saveEndpoint = isFamilyFlow
        ? `/api/family_reminder_log/${family_id_for_count}`
        : `/api/create_reminder_log/${invoice_id}`;

      // Keep payload shape consistent; family API expects these exact keys.
      const payload = {
        reminder_number: reminderNumber ?? 0,
        client_reply: clientReply,
        next_reminder_date: formattedDate,
      };

      if (use_family_count_api && !family_id_for_count) {
        setErrors((prev) => ({
          ...prev,
          apiError: "Family Group context is missing for saving reminder.",
        }));
        setIsLoading(false);
        return;
      }

      const response = await axiosInstance.post(saveEndpoint, payload);

      console.log("Reminder saved successfully:", response.data);
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error saving reminder:", error);
      setErrors((prev) => ({
        ...prev,
        apiError: "There was an error saving the reminder.",
      }));
    } finally {
      setIsLoading(false); // Stop loader
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-container-reminder">
        <div className="popup-header">
          <div>
            <h2 className="popup-title">Reminder History</h2>
            <p className="popup-subtitle">
              Update follow-up details and schedule the next reminder.
            </p>
          </div>
        </div>

        <div className="popup-content">
          <div className="popup-reminder-summary">
            <span className="popup-summary-label">Number Of Reminders</span>
            <span className="popup-summary-value">
              {reminderNumber !== null ? reminderNumber : "Loading..."}
            </span>
          </div>

          <div className="popup-form-stack">
            <div className="popup-row">
              <label className="popup-label">Next Reminder Date:</label>
              <input
                type="text"
                className="popup-input popup-reminder-date"
                value={nextReminderDate}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow only numbers and hyphens, max 10 characters (DD-MM-YYYY)
                  const cleaned = value.replace(/[^0-9-]/g, "").slice(0, 10);
                  setNextReminderDate(cleaned);

                  // Clear error while typing
                  setErrors((prev) => ({ ...prev, pastDate: "" }));
                }}
                placeholder="Enter Date (eg. DD-MM-YYYY)"
                maxLength={10}
              />
              {errors.pastDate && (
                <span className="error-message popup-error-message">
                  {errors.pastDate}
                </span>
              )}
              {errors.nextReminderDate && (
                <span className="error-message popup-error-message">
                  {errors.nextReminderDate}
                </span>
              )}
            </div>

            <div className="popup-row">
              <label className="popup-label">Client Reply:</label>
              <textarea
                className="popup-input popup-textarea"
                value={clientReply}
                onChange={(e) => setClientReply(e.target.value)}
                placeholder="Enter reply..."
                rows="4"
              />
              {errors.clientReply && (
                <span className="error-message popup-error-message">
                  {errors.clientReply}
                </span>
              )}
            </div>

            {errors.apiError && (
              <span className="error-message popup-error-message">
                {errors.apiError}
              </span>
            )}
          </div>
        </div>

        <div className="popup-footer">
          <button className="popup-btn cancel-btn" onClick={onClose}>
            CANCEL
          </button>
          <button
            className="popup-btn save-btn"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="btn-spinner"></span>
                SAVING...
              </>
            ) : (
              "SAVE"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TodaysReminderPopup;
